import { useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Edit, ShoppingCart, Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { CustomerForm } from '@/components/forms/customer-form'
import { PaymentForm } from '@/components/forms/payment-form'
import {
    useCustomer,
    useCustomerCreditStatus,
    useCustomerDebts,
    useCustomerOrders,
    useDeleteCustomer,
    useRecordCustomerPayment,
} from '@/hooks/use-customer-query'
import { m } from '@/lib/paraglide/messages'
import { formatVnd } from '@/lib/format'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/customers/$customerId',
)({
    component: CustomerDetailPage,
})

function CustomerDetailPage() {
    const { customerId } = Route.useParams()
    const navigate = useNavigate()
    const [tab, setTab] = useState<'info' | 'orders' | 'debts'>('info')
    const [editOpen, setEditOpen] = useState(false)
    const [payOpen, setPayOpen] = useState(false)
    const [deleteOpen, setDeleteOpen] = useState(false)

    const { data: customer, isLoading } = useCustomer(customerId)
    const { data: ordersData } = useCustomerOrders(customerId)
    const { data: debtsData } = useCustomerDebts(customerId)
    const { data: creditStatus } = useCustomerCreditStatus(customerId)
    const recordPayment = useRecordCustomerPayment(customerId)
    const deleteMutation = useDeleteCustomer()

    async function handleDelete() {
        await deleteMutation.mutateAsync(customerId)
        setDeleteOpen(false)
        void navigate({ to: '/customers' })
    }

    if (isLoading) {
        return (
            <div className="text-muted-foreground text-sm">
                {m.common_loading()}
            </div>
        )
    }
    if (!customer) {
        return (
            <div className="text-muted-foreground text-sm">
                {m.customer_not_found_msg()}
            </div>
        )
    }

    const orders = ordersData?.data ?? []
    const debts = debtsData?.data ?? []

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center gap-3">
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => void navigate({ to: '/customers' })}
                >
                    <ArrowLeft className="size-4" />
                </Button>
                <div className="flex-1">
                    <h1 className="text-xl font-semibold">{customer.name}</h1>
                    <p className="text-sm text-muted-foreground font-mono">
                        {customer.code}
                    </p>
                </div>
                <div className="flex gap-2">
                    <Link
                        to="/orders/new"
                        search={{
                            customer_id: customerId,
                            customer_name: customer.name,
                            customer_phone: customer.phone ?? '',
                        }}
                    >
                        <Button size="sm">
                            <ShoppingCart className="size-4" />
                            {m.dashboard_create_order()}
                        </Button>
                    </Link>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditOpen(true)}
                    >
                        <Edit className="size-4" />
                        {m.customer_edit_btn()}
                    </Button>
                    <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => setDeleteOpen(true)}
                    >
                        <Trash2 className="size-4" />
                        {m.common_delete()}
                    </Button>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 border-b">
                {(['info', 'orders', 'debts'] as const).map((t) => (
                    <button
                        key={t}
                        onClick={() => setTab(t)}
                        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                            tab === t
                                ? 'border-primary text-primary'
                                : 'border-transparent text-muted-foreground hover:text-foreground'
                        }`}
                    >
                        {t === 'info'
                            ? m.customer_tab_info()
                            : t === 'orders'
                              ? m.nav_orders()
                              : m.customer_tab_debts()}
                    </button>
                ))}
            </div>

            {/* Info tab */}
            {tab === 'info' && (
                <div className="grid gap-4 sm:grid-cols-2">
                    <Card>
                        <CardHeader>
                            <CardTitle>
                                {m.customer_contact_info_title()}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2 text-sm">
                            <Row
                                label={m.customer_phone()}
                                value={customer.phone ?? '—'}
                            />
                            <Row label="Email" value={customer.email ?? '—'} />
                            <Row
                                label={m.customer_address()}
                                value={customer.address ?? '—'}
                            />
                            <Row
                                label={m.customer_tax_code()}
                                value={customer.tax_code ?? '—'}
                            />
                            <Row
                                label={m.customer_group()}
                                value={customer.customer_group?.name ?? '—'}
                            />
                            <Row
                                label={m.product_status()}
                                value={
                                    <Badge
                                        variant={
                                            customer.is_active
                                                ? 'success'
                                                : 'secondary'
                                        }
                                    >
                                        {customer.is_active
                                            ? m.customer_active()
                                            : m.customer_inactive()}
                                    </Badge>
                                }
                            />
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader>
                            <CardTitle>
                                {m.customer_debt_card_title()}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2 text-sm">
                            <Row
                                label={m.customer_credit_limit()}
                                value={formatVnd(customer.credit_limit)}
                            />
                            {creditStatus && (
                                <>
                                    <Row
                                        label={m.customer_total_debt()}
                                        value={formatVnd(
                                            creditStatus.current_debt,
                                        )}
                                    />
                                    <Row
                                        label={m.customer_remaining_credit()}
                                        value={
                                            creditStatus.is_unlimited
                                                ? m.customer_unlimited_credit()
                                                : formatVnd(
                                                      creditStatus.available_credit,
                                                  )
                                        }
                                    />
                                </>
                            )}
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Orders tab */}
            {tab === 'orders' && (
                <div className="rounded-md border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{m.order_code()}</TableHead>
                                <TableHead>{m.order_created_date()}</TableHead>
                                <TableHead>{m.order_total()}</TableHead>
                                <TableHead>{m.order_status()}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {orders.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={4}
                                        className="text-center text-muted-foreground py-6"
                                    >
                                        {m.customer_no_orders()}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                orders.map((o) => (
                                    <TableRow key={o.id}>
                                        <TableCell>
                                            <Link
                                                to="/orders/$orderId"
                                                params={{
                                                    orderId: o.id,
                                                }}
                                                className="font-mono text-xs text-primary hover:underline"
                                            >
                                                {o.order_code}
                                            </Link>
                                        </TableCell>
                                        <TableCell className="text-sm">
                                            {new Date(
                                                o.created_at,
                                            ).toLocaleDateString('vi-VN')}
                                        </TableCell>
                                        <TableCell>
                                            {formatVnd(o.total_amount)}
                                        </TableCell>
                                        <TableCell>{o.status}</TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            )}

            {/* Debts tab */}
            {tab === 'debts' && (
                <div className="space-y-3">
                    <div className="flex justify-end">
                        <Button size="sm" onClick={() => setPayOpen(true)}>
                            {m.order_record_payment()}
                        </Button>
                    </div>
                    <div className="rounded-md border">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>
                                        {m.customer_total_debt()}
                                    </TableHead>
                                    <TableHead>
                                        {m.customer_debt_paid_label()}
                                    </TableHead>
                                    <TableHead>
                                        {m.customer_remaining_credit()}
                                    </TableHead>
                                    <TableHead>{m.order_status()}</TableHead>
                                    <TableHead>
                                        {m.order_created_date()}
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {debts.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={5}
                                            className="text-center text-muted-foreground py-6"
                                        >
                                            {m.customer_no_debts()}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    debts.map((d) => (
                                        <TableRow key={d.id}>
                                            <TableCell>
                                                {formatVnd(d.total_amount)}
                                            </TableCell>
                                            <TableCell>
                                                {formatVnd(d.paid_amount)}
                                            </TableCell>
                                            <TableCell className="font-medium">
                                                {formatVnd(d.remaining_amount)}
                                            </TableCell>
                                            <TableCell>
                                                <Badge
                                                    variant={
                                                        d.status === 'paid'
                                                            ? 'success'
                                                            : d.status ===
                                                                'partial'
                                                              ? 'warning'
                                                              : 'destructive'
                                                    }
                                                >
                                                    {d.status === 'paid'
                                                        ? m.customer_debt_paid_label()
                                                        : d.status === 'partial'
                                                          ? m.customer_debt_partial_label()
                                                          : m.customer_debt_unpaid_label()}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-sm">
                                                {new Date(
                                                    d.created_at,
                                                ).toLocaleDateString('vi-VN')}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            )}

            {/* Edit dialog */}
            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>
                            {m.customer_edit_dialog_title()}
                        </DialogTitle>
                    </DialogHeader>
                    <CustomerForm
                        customer={customer}
                        onSuccess={() => setEditOpen(false)}
                        onCancel={() => setEditOpen(false)}
                    />
                </DialogContent>
            </Dialog>

            {/* Payment dialog */}
            <Dialog open={payOpen} onOpenChange={setPayOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{m.order_record_payment()}</DialogTitle>
                    </DialogHeader>
                    <PaymentForm
                        onSubmit={async (data) => {
                            await recordPayment.mutateAsync({
                                amount: data.amount,
                                payment_method: data.method,
                                notes: data.reference,
                            })
                            setPayOpen(false)
                        }}
                        onCancel={() => setPayOpen(false)}
                        isPending={recordPayment.isPending}
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {m.customer_delete_dialog_title()}
                        </DialogTitle>
                        <DialogDescription>
                            {m.customer_delete_dialog_confirm_prefix()}{' '}
                            <b>{customer.name}</b>
                            {m.customer_delete_dialog_confirm_suffix()}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setDeleteOpen(false)}
                            disabled={deleteMutation.isPending}
                        >
                            {m.common_cancel()}
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDelete}
                            disabled={deleteMutation.isPending}
                        >
                            {m.common_delete()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <div className="flex justify-between gap-2">
            <span className="text-muted-foreground shrink-0">{label}</span>
            <span className="text-right">{value}</span>
        </div>
    )
}
